import PropTypes from 'prop-types';
import { isNull } from 'lodash';
import {
  memo, useState, useCallback, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import ErrorTooltip from '../../../ErrorTooltip';
import SingleSelectAutocompleteFilter from '../../../Filters/SingleSelectAutocompleteFilter';
import DateFilter from '../../../Filters/DateSelectorFilter';
import PassengersFilter from '../../FilterBar/PassengersFilter';
import {
  SearchStatus, AutocompleteType, SearchModes, TravelTypes,
} from '../../../../constants';
import TwAutocomplete from '../../../TwAutocomplete';
import { useAutocompleteStyles } from '../styles';
import {
  customInventorySearchRequest, resetSearchResults, setCustomInventorySearchObjFromQueryString,
  setTripSearchInputs, setTripSearchObj,
} from '../../../../store/slices/tripSearch';
import { autocompleteRequest } from '../../../../store/slices/autocomplete';
import { resetTrips } from '../../../../store/slices/trip';
import setURLParams from '../../../../utils/setURLParams';
import useSearchObjFromQueryString from '../../../../hooks/useSearchObjFromQueryString';
import { customInventorySearchTripId, updateCustomInventorySearchObj } from '../../../../store/slices/customInventory';
import customInventorySearchInput from '../../../../helpers/selectors/customInventorySearchInput';

const CustomInventorySearchBar = ({ tripId, tabContent }) => {
  const isTripSearch = !tripId;
  const router = useRouter();
  const dispatch = useDispatch();
  const { travelAgency } = useSelector((store) => store.user);
  const initialFormValidations = { startDate: null, endDate: null, destinations: null };
  const [formValidations, setFormValidations] = useState(initialFormValidations);
  const { searchMode: searchModeQuery } = router.query;
  const selectedSearchMode = useSelector((store) => store.tripSearch.selectedSearchMode);
  const { isLoggedIn } = useSelector((store) => store.user);
  const {
    searchInputs,
    // searchStatus,
  } = useSelector(customInventorySearchInput(tripId, isTripSearch));
  const tripSearch = useSelector((store) => store.tripSearch);
  const {
    flightOriginAutocompleteResults,
  } = useSelector((store) => store.autocomplete);
  const autocompleteClasses = useAutocompleteStyles({
    doAddBorderInRoot: true,
    minHeight: '100%',
    withLeftRadius: true,
    withRightRadius: !isTripSearch,
    noPaddingTop: true,
  });

  const autocompletePropertyName = `${'customInventory'}AutocompleteResults`;
  const autocompleteResults = useSelector((store) => store.autocomplete[autocompletePropertyName]);

  const startDateLabel = (tabContent || {}).searchFormStartDateLabel || 'Start date';
  const endDateLabel = (tabContent || {}).searchFormEndDateLabel || 'End date';
  const destinationLabel = (tabContent || {}).searchFormDestinationLabel || 'Select destination(s)';
  const isDestinationRequired = (tabContent || {}).isDestinationRequired || false;

  const handleChange = (propertyName, value) => {
    const payload = {
      ...tripSearch.searchInputs,
    };
    if (isTripSearch) {
      payload.customInventory = {
        ...payload.customInventory,
      };
    } else {
      payload.customInventory = {
        ...searchInputs,
      };
    }
    payload.customInventory[propertyName] = value;
    if (propertyName === 'startDate' && (!payload.customInventory.endDate || dayjs(value).diff(payload.customInventory.endDate, 'day') >= 0)) {
      payload.customInventory.endDate = dayjs(value).add(1, 'day').format('YYYY-MM-DD');
    }
    if (isTripSearch) {
      dispatch(setTripSearchInputs({ ...payload }));
    } else {
      dispatch(updateCustomInventorySearchObj({
        tripId,
        searchObj: {
          ...payload.customInventory,
        },
      }));
    }
  };

  const handleAutocompleteChange = useCallback(
    (autocompleteTypes, responseType) => (query) => {
      dispatch(
        autocompleteRequest({
          autocompleteTypes,
          responseType,
          query,
        }),
      );
    },
    [dispatch],
  );

  const validateSearch = (obj, doShowValidationErrors = true) => {
    const validationObj = { ...initialFormValidations };
    let isValid = true;
    if (!obj) isValid = false;
    if (isDestinationRequired && (!obj?.destinations || !obj?.destinations?.length)) {
      validationObj.selectedAddress = `${destinationLabel} is required.`;
      isValid = false;
    }
    if (!obj?.startDate) {
      validationObj.checkInDate = `${startDateLabel} is required.`;
      isValid = false;
    }
    if (!obj?.endDate) {
      validationObj.checkOutDate = `${endDateLabel} is required.`;
      isValid = false;
    }
    if (doShowValidationErrors) setFormValidations(validationObj);
    return isValid;
  };

  const handleSearch = (event, doShowValidationErrors = true) => {
    if (event) event.preventDefault();
    if (validateSearch(isTripSearch ? tripSearch.searchInputs.customInventory
      : searchInputs, doShowValidationErrors)) {
      if (isTripSearch) {
        dispatch(resetTrips());
        dispatch(resetSearchResults());
        dispatch(customInventorySearchRequest({ isTripSearch }));
        dispatch(setTripSearchObj({ ...searchInputs.customInventory }));
        setURLParams({
          searchObj: isTripSearch ? tripSearch.searchInputs : searchInputs,
          searchedMode: SearchModes.CUSTOM,
          router,
        });
      } else dispatch(customInventorySearchTripId({ isTripSearch, tripId }));
    }
  };

  const handleResetValidation = () => {
    setFormValidations(initialFormValidations);
  };

  useEffect(() => {
    if (!autocompleteResults || !autocompleteResults.length) {
      dispatch(
        autocompleteRequest({
          autocompleteTypes: AutocompleteType.CUSTOM,
          responseType: autocompletePropertyName,
          key: travelAgency && travelAgency.searchbarTab.id,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSearchObjFromQueryString((searchObjFromQueryString) => {
    if (tripSearch.searchStatus !== SearchStatus.INITIAL) return;
    if ((searchModeQuery || SearchModes.CRUISE_TRIP) === selectedSearchMode) {
      dispatch(setCustomInventorySearchObjFromQueryString(searchObjFromQueryString));
      if (validateSearch(searchObjFromQueryString, false)) {
        dispatch(resetTrips());
        dispatch(customInventorySearchRequest({ isTripSearch: true }));
      }
    } else {
      dispatch(resetSearchResults({ isLoggedIn }));
      handleSearch(null, false);
    }
  }, undefined);

  return (
    <form onSubmit={handleSearch} onFocus={handleResetValidation}>
      <div className="mt-2 p-2 bord">
        <div className={clsx('text-theme', { 'grid xl:grid-cols-10 grid-cols-12 mt-2': isTripSearch, 'flex gap-2 flex-wrap': !isTripSearch })}>
          <ErrorTooltip
            title={formValidations.selectedAddress || ''}
            open={!isNull(formValidations.selectedAddress)}
            arrow
          >
            <div className={clsx('xl:col-span-4 col-span-4', { 'w-56': !isTripSearch })}>
              <TwAutocomplete
                options={autocompleteResults}
                placeholderText={destinationLabel}
                onChange={(val) => handleChange('destinations', val)}
                classes={autocompleteClasses}
                values={searchInputs?.destinations || []}
                disableCloseOnSelect
                isMultiple
              />
            </div>
          </ErrorTooltip>
          <ErrorTooltip
            title={formValidations.checkInDate || ''}
            open={!isNull(formValidations.checkInDate)}
            arrow
          >
            <div className="xl:col-span-2 col-span-3">
              <DateFilter
                onChange={(val) => handleChange('startDate', val)}
                label={startDateLabel}
                value={searchInputs?.startDate}
                defaultValue={searchInputs?.startDate}
                variant={isTripSearch ? 'outline-no-radius' : 'outline'}
                filterButtonMaxWidth={clsx('max-w-full', { 'w-full': isTripSearch })}
              />
            </div>
          </ErrorTooltip>
          <ErrorTooltip
            title={formValidations.checkOutDate || ''}
            open={!isNull(formValidations.checkOutDate)}
            arrow
          >
            <div className="xl:col-span-2 col-span-3">
              <DateFilter
                onChange={(val) => handleChange('endDate', val)}
                label={endDateLabel}
                value={searchInputs?.endDate}
                defaultValue={searchInputs?.endDate}
                minDate={searchInputs?.startDate}
                variant={isTripSearch ? 'outline-no-radius' : 'outline'}
                filterButtonMaxWidth={clsx('max-w-full', { 'w-full': isTripSearch })}
              />
            </div>
          </ErrorTooltip>
          <div className="col-span-2">
            <button
              type="submit"
              className={clsx('rounded-r-2xl bg-searchButton-0 text-searchbtn-0 font-bold text-center w-full h-full', { 'rounded-l-2xl px-5': !isTripSearch })}
            >
              Search
            </button>
          </div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap text-white">
          { !!isTripSearch && <PassengersFilter />}
          {(!travelAgency || !travelAgency.tripkitEnabledTransportModes
        || travelAgency.tripkitEnabledTransportModes.indexOf(TravelTypes.flight) > -1)
        && !!isTripSearch
        && (
          <SingleSelectAutocompleteFilter
            onChange={(val) => handleChange('origin', val)}
            options={flightOriginAutocompleteResults}
            onInputChange={handleAutocompleteChange(AutocompleteType.FLIGHT_ORIGIN, 'flightOriginAutocompleteResults')}
            value={searchInputs?.origin}
            label="Flight origin"
            filterButtonMaxWidth="max-w-filter"
            widthInside="max-w-85"
          />
        )}
        </div>
      </div>
    </form>
  );
};

CustomInventorySearchBar.propTypes = {
  tripId: PropTypes.string,
  tabContent: PropTypes.shape().isRequired,
};

CustomInventorySearchBar.defaultProps = {
  tripId: undefined,
};

export default memo(CustomInventorySearchBar);
